import { zodResolver } from "@hookform/resolvers/zod";
import { PenLine } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import FormWrapper from "@/components/reusables/custom-form-fields/FormWrapper";
import { Button } from "@/components/ui/button";
import { Form, FormField } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { basicValidator } from "@/lib/validators";
import { useGetProjectsByStatusQuery } from "@/redux/api/projectsQuery";

type TableDataEditorProps = {
  children: React.JSX.Element;
  customerId: string;
  projectId: string;
  customerList: { label: string; value: string }[];
  handleSubmit: (data: { customerId?: string; projectId?: string }) => void;
};

export const CustomerUpdateSchema = z.object({
  customerId: basicValidator(true, "Please select a customer"),
  projectId: basicValidator(true, "Please select a project"),
});

export type TCustomerUpdateSchema = z.infer<typeof CustomerUpdateSchema>;

const CustomerDataEditor = ({
  children,
  customerId,
  projectId,
  customerList,
  handleSubmit,
}: TableDataEditorProps): React.JSX.Element => {
  const [isMutate, setIsMutate] = useState(false);
  const form = useForm<TCustomerUpdateSchema>({
    resolver: zodResolver(CustomerUpdateSchema),
    values: {
      customerId,
      projectId,
    },
  });

  const handleExit = (): void => {
    setIsMutate(false);
  };

  const { data: projects } = useGetProjectsByStatusQuery({
    status: "",
  });
  const custId = form.getValues("customerId");

  const getProjectList = (
    customerId: string,
  ): { label: string; value: string }[] => {
    return projects?.data
      ? projects?.data
          ?.filter(
            (project) =>
              project?.status !== "ARCHIVE" &&
              project.customerId === customerId,
          )
          .map((item) => ({
            label: item?.name || "",
            value: item?.id || "",
          }))
      : [];
  };

  const onSubmit = (data: TCustomerUpdateSchema): void => {
    // validator to check if the corresponding project exists
    if (data.customerId) {
      const isProjectExist = getProjectList(data.customerId).find(
        (item) => item.value === data.projectId,
      );
      if (!isProjectExist) {
        form.setError("projectId", {
          type: "manual",
          message: "Please select a valid project",
        });

        return;
      }
    }
    handleSubmit(data);
  };

  return (
    <Popover open={isMutate} onOpenChange={handleExit}>
      <PopoverTrigger asChild>
        <div className='flex justify-between w-full cursor-pointer'>
          <div className='flex items-center w-full justify-between'>
            {children}
          </div>
          <div
            className='invisible [td:hover_&]:visible'
            onClick={(e) => {
              e.stopPropagation();
              setIsMutate(true);
            }}
          >
            <PenLine size={18} />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className='z-50'>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='w-full space-y-4'>
              <FormField
                control={form.control}
                name={"customerId"}
                render={({ field }) => (
                  <FormWrapper label={"Customer "}>
                    <FormSelectSingle
                      placeholder={`Please Select Customer`}
                      field={field}
                      className=' w-full'
                      options={customerList}
                    />
                  </FormWrapper>
                )}
              />{" "}
              <FormField
                control={form.control}
                name={"projectId"}
                render={({ field }) => (
                  <FormWrapper label={"Project"}>
                    <FormSelectSingle
                      disabled={!custId}
                      placeholder={`Please Select Project`}
                      field={field}
                      className=' w-full'
                      options={getProjectList(custId ?? "")}
                    />
                  </FormWrapper>
                )}
              />
            </div>
            {Object.entries(form.formState.dirtyFields)?.length ? (
              <div className='flex flex-row-reverse my-4'>
                <Button variant={"light"} type='submit'>
                  Save
                </Button>
                <Button variant={"ghost"} onClick={() => form.reset()}>
                  Reset
                </Button>
              </div>
            ) : null}
          </form>
        </Form>
      </PopoverContent>
    </Popover>
  );
};

export default CustomerDataEditor;
